import { sumBy as _sumBy, times as _times, uniq as _uniq } from 'lodash-es'
import { Factory, Model } from 'miragejs'
import type { MockServer } from '@@/api/mock-server/server.js'
import {
  PROVET_PAY_PAYOUT_ENDPOINTS,
  PROVET_PAY_PAYOUT_GROUPABLE_COLUMNS,
  PROVET_PAY_PAYOUT_RESOURCE_TYPES,
} from '@/constants/reports/provetPayPayoutReport.js'
import { filterable, pageable, sortable } from '../utils.js'
import { rand, randAmount, randNumber } from '@ngneat/falso'
import { isAfter, isBefore } from '@/utils/date/validation.js'
import type {
  ProvetPayPayoutFilters,
  ProvetPayPayoutItemAttributes,
} from '@/models/reports/provetPayPayoutReport.js'
import { toNumber } from '@/utils/number.js'

const MOCK_KEY = 'provetPayPayoutReport'

const CLINIC_LOCATIONS_POOL = [
  'Pawsome',
  'Bath Clinic',
  'PetCool',
  'City Vet Centre',
]

const PAYOUT_DATES_POOL = [
  '2026-01-10',
  '2026-01-17',
  '2026-01-24',
  '2026-02-01',
  '2026-02-08',
  '2026-02-15',
  '2026-02-22',
  '2026-03-01',
  '2026-03-05',
  '2026-03-10',
  '2026-03-15',
  '2026-03-18',
  '2026-03-22',
  '2026-03-25',
  '2026-03-28',
]

export const model = Model.extend({})

export const factory = Factory.extend<ProvetPayPayoutItemAttributes>({
  payout_psp_reference() {
    return `BA${randNumber({ max: 9_999_999_999, min: 1 })
      .toString()
      .padStart(16, '0')}`
  },
  channel() {
    return rand(['POS', 'ECOM'])
  },
  clinic_location() {
    return rand(CLINIC_LOCATIONS_POOL)
  },
  currency() {
    return 'EUR'
  },
  fees() {
    const gross = Math.abs(toNumber(this.gross_amount))
    const feeRate = randNumber({ max: 300, min: 150 }) / 10000
    return -(gross * feeRate)
  },
  gross_amount() {
    return randAmount({ max: 70000, min: 500 })
  },
  net_payout_amount() {
    const gross = toNumber(this.gross_amount)
    const fees = Math.abs(toNumber(this.fees))
    const refunds = Math.abs(toNumber(this.refunds))
    return gross - fees - refunds
  },
  payout_ts_utc() {
    return `${rand(PAYOUT_DATES_POOL)}T00:00:00Z`
  },
  refunds() {
    const hasRefund = randNumber({ max: 100, min: 1 }) <= 30
    if (!hasRefund) {
      return 0
    }
    const gross = Math.abs(toNumber(this.gross_amount))
    return -(gross * (randNumber({ max: 15, min: 1 }) / 100))
  },
  transaction_count() {
    return randNumber({ max: 71, min: 1 })
  },
})

export const seeds = (server: MockServer) => {
  _times(80, () => {
    server.create(MOCK_KEY)
  })
}

const includesInCommaSeparated = (
  itemValue: string | null | undefined,
  filterValue: string,
) =>
  filterValue
    .split(',')
    .some(
      (v) =>
        v.localeCompare(itemValue ?? '', undefined, { sensitivity: 'base' }) ===
        0,
    )

export const routes = (server: MockServer) => {
  server.get(PROVET_PAY_PAYOUT_ENDPOINTS.LIST, (schema, request) => {
    const groupBy = request.queryParams?.group_by as
      | 'channel'
      | 'clinic_location'
      | undefined

    const results = schema.all(MOCK_KEY)

    const sorted = sortable(results, request, {
      channel: (item) => item.attrs.channel,
      clinic_location: (item) => item.attrs.clinic_location,
      fees: (item) => toNumber(item.attrs.fees),
      gross_amount: (item) => toNumber(item.attrs.gross_amount),
      net_payout_amount: (item) => toNumber(item.attrs.net_payout_amount),
      payout_ts_utc: (item) => item.attrs.payout_ts_utc,
      refunds: (item) => toNumber(item.attrs.refunds),
      transaction_count: (item) => item.attrs.transaction_count,
    })

    const filtered = filterable(sorted, request, {
      'channel.in': (item, value) =>
        includesInCommaSeparated(item.attrs.channel, value),
      'clinic_location.in': (item, value) =>
        includesInCommaSeparated(item.attrs.clinic_location, value),
      'payout_ts_utc.gte': (item, value) =>
        !isBefore(item.attrs.payout_ts_utc, value),
      'payout_ts_utc.lte': (item, value) =>
        !isAfter(item.attrs.payout_ts_utc, value),
    })

    const allFilteredModels = filtered.models

    const aggregation = {
      currency: 'EUR',
      fees: _sumBy(allFilteredModels, (i) => toNumber(i.attrs.fees)).toFixed(2),
      gross_amount: _sumBy(allFilteredModels, (i) =>
        toNumber(i.attrs.gross_amount),
      ).toFixed(2),
      net_payout_amount: _sumBy(allFilteredModels, (i) =>
        toNumber(i.attrs.net_payout_amount),
      ).toFixed(2),
      refunds: _sumBy(allFilteredModels, (i) =>
        toNumber(i.attrs.refunds),
      ).toFixed(2),
    }

    if (groupBy) {
      const groupedData = allFilteredModels.reduce(
        (groups, item) => {
          const groupValue = String(item.attrs[groupBy] || '')
          if (!groups[groupValue]) {
            groups[groupValue] = { groupValue, items: [] }
          }
          groups[groupValue].items.push(item)
          return groups
        },
        {} as Record<
          string,
          { groupValue: string; items: typeof allFilteredModels }
        >,
      )

      const sortParam = request.queryParams?.sort as string | undefined
      const sortDesc = sortParam?.startsWith('-') ?? false

      const sortedGroups = Object.entries(groupedData)
        .map(([key, group]) => ({
          key,
          result: {
            id: `group:${group.groupValue}`,
            type: PROVET_PAY_PAYOUT_RESOURCE_TYPES.GROUP,
            attributes: {
              batches: group.items
                .sort((a, b) =>
                  b.attrs.payout_ts_utc.localeCompare(a.attrs.payout_ts_utc),
                )
                .map((item) => ({ ...item.attrs })),
              group_field: groupBy,
              group_value: group.groupValue,
            },
          },
        }))
        .sort((a, b) => {
          const cmp = String(a.key).localeCompare(String(b.key))
          return sortDesc ? -cmp : cmp
        })
        .map((g) => g.result)

      const pageNumberParam = request.queryParams?.['page[number]']
      const pageSizeParam = request.queryParams?.['page[size]']
      const pageNumber = parseInt(
        Array.isArray(pageNumberParam)
          ? pageNumberParam[0]
          : pageNumberParam || '1',
      )
      const pageSize = parseInt(
        Array.isArray(pageSizeParam) ? pageSizeParam[0] : pageSizeParam || '50',
      )
      const startIndex = (pageNumber - 1) * pageSize
      const paginatedGroups = sortedGroups.slice(
        startIndex,
        startIndex + pageSize,
      )

      return {
        data: paginatedGroups,
        meta: {
          aggregation,
          pagination: {
            count: sortedGroups.length,
            page: pageNumber,
            pages: Math.ceil(sortedGroups.length / pageSize),
          },
        },
      }
    }

    const { data, meta } = pageable(filtered, request)

    return {
      data: data.models.map((item) => ({
        id: item.id,
        attributes: { ...item.attrs },
        type: PROVET_PAY_PAYOUT_RESOURCE_TYPES.PAYOUT,
      })),
      meta: {
        aggregation,
        pagination: meta,
      },
    }
  })

  server.get(PROVET_PAY_PAYOUT_ENDPOINTS.GROUPS, (schema, request) => {
    const groupByParam = request.queryParams?.group_by
    const groupBy = PROVET_PAY_PAYOUT_GROUPABLE_COLUMNS.includes(
      groupByParam as (typeof PROVET_PAY_PAYOUT_GROUPABLE_COLUMNS)[number],
    )
      ? (groupByParam as (typeof PROVET_PAY_PAYOUT_GROUPABLE_COLUMNS)[number])
      : undefined

    if (!groupBy) {
      return {
        data: [],
        meta: { pagination: { page: 1, pages: 1, count: 0 } },
      }
    }

    const results = schema.all(MOCK_KEY)
    const sorted = sortable(results, request, {
      channel: (item) => item.attrs.channel,
      clinic_location: (item) => item.attrs.clinic_location,
      fees: (item) => toNumber(item.attrs.fees),
      gross_amount: (item) => toNumber(item.attrs.gross_amount),
      net_payout_amount: (item) => toNumber(item.attrs.net_payout_amount),
      payout_ts_utc: (item) => item.attrs.payout_ts_utc,
      refunds: (item) => toNumber(item.attrs.refunds),
      transaction_count: (item) => item.attrs.transaction_count,
    })
    const filtered = filterable(sorted, request, {
      'channel.in': (item, value) =>
        includesInCommaSeparated(item.attrs.channel, value),
      'clinic_location.in': (item, value) =>
        includesInCommaSeparated(item.attrs.clinic_location, value),
      'payout_ts_utc.gte': (item, value) =>
        !isBefore(item.attrs.payout_ts_utc, value),
      'payout_ts_utc.lte': (item, value) =>
        !isAfter(item.attrs.payout_ts_utc, value),
    })

    const groupedData = filtered.models.reduce(
      (groups, item) => {
        const groupValue = String(item.attrs[groupBy] || '')
        if (!groups[groupValue]) {
          groups[groupValue] = { groupValue, items: [] }
        }
        groups[groupValue].items.push(item)
        return groups
      },
      {} as Record<
        string,
        { groupValue: string; items: typeof filtered.models }
      >,
    )

    const sortParam = request.queryParams?.sort as string | undefined
    const sortDesc = sortParam?.startsWith('-') ?? false

    const groupsWithAggregations = Object.entries(groupedData).map(
      ([key, group]) => ({
        key,
        result: {
          id: `${groupBy}:${group.groupValue ?? key}`,
          type: PROVET_PAY_PAYOUT_RESOURCE_TYPES.GROUP,
          attributes: {
            currency: 'EUR',
            fees: _sumBy(group.items, (i) => toNumber(i.attrs.fees)),
            gross_amount: _sumBy(group.items, (i) =>
              toNumber(i.attrs.gross_amount),
            ),
            group_by: groupBy,
            group_value: group.groupValue,
            net_payout_amount: _sumBy(group.items, (i) =>
              toNumber(i.attrs.net_payout_amount),
            ),
            refunds: _sumBy(group.items, (i) => toNumber(i.attrs.refunds)),
            payout_count: group.items.length,
          },
        },
      }),
    )

    const sortedGroups = groupsWithAggregations
      .sort((a, b) => {
        const cmp = String(a.key).localeCompare(String(b.key))
        return sortDesc ? -cmp : cmp
      })
      .map((g) => g.result)

    const pageNumberParam = request.queryParams?.['page[number]']
    const pageSizeParam = request.queryParams?.['page[size]']
    const pageNumber = parseInt(
      Array.isArray(pageNumberParam)
        ? pageNumberParam[0]
        : pageNumberParam || '1',
    )
    const pageSize = parseInt(
      Array.isArray(pageSizeParam) ? pageSizeParam[0] : pageSizeParam || '50',
    )
    const startIndex = (pageNumber - 1) * pageSize
    const paginatedGroups = sortedGroups.slice(
      startIndex,
      startIndex + pageSize,
    )

    return {
      data: paginatedGroups,
      meta: {
        pagination: {
          count: sortedGroups.length,
          page: pageNumber,
          pages: Math.ceil(sortedGroups.length / pageSize),
        },
      },
    }
  })

  server.get(PROVET_PAY_PAYOUT_ENDPOINTS.FILTERS, (schema) => {
    const allPayouts = schema.all(MOCK_KEY).models

    const filters: ProvetPayPayoutFilters = {
      channel: _uniq(allPayouts.map((p) => p.attrs.channel)),
      clinic_location: _uniq(allPayouts.map((p) => p.attrs.clinic_location)),
    }

    return {
      data: {
        id: 'filters',
        attributes: filters,
        type: PROVET_PAY_PAYOUT_RESOURCE_TYPES.FILTERS,
      },
    }
  })
}
